import { getDropdownOptions, getDropdownValue } from "helpers/DropdownHelper";
import {
    Button,
    Dropdown,
    Field,
    Input,
    Loading,
    Modal,
    ModalFooter,
    ModalHeader,
    NotificationPriorityTranslation,
    NotificationSendingEntityTranslation,
    NotificationSeverityTranslation,
    NotificationTypeTranslation,
    onChange,
    TextArea,
    ToggleSwitch,
    validate,
    ValidationErrors,
} from "modules/common";
import { ProfessionalField, ProfessionalFieldFilterFields } from "modules/common/constants/professionalFieldTypes";
import React, { useEffect, useState } from "react";
import {
    CreateOrUpdateNotificationTemplateView,
    NotificationTemplate,
    NotificationTemplateView,
} from "services/communication";
import { useCreateOrUpdateNotificationTemplate } from "../hooks";

const ERROR_MESSAGES: { [P in keyof NotificationTemplate]?: string } = {
    name: "Naziv je obavezan",
    subject: "Naslov poruke je obavezan",
    body: "Poruka je obavezna",
    type: "Način slanja je obavezan",
    severity: "Vrsta poruke je obavezna",
    priority: "Prioritet je obavezan",
    professionalField: "Stručno područje je obavezno",
    sendingEntity: "Pošiljatelj je obavezan",
};

const FIELD_ID: { [P in keyof NotificationTemplate]?: string } = {
    name: "name",
    subject: "subject",
    body: "body",
    type: "type",
    severity: "severity",
    priority: "priority",
    professionalField: "professionalField",
    sendingEntity: "sendingEntity",
};

interface Props {
    isActive: boolean;
    toggleModal: () => void;
    selectedNotificationTemplate?: NotificationTemplateView;
    isLoading?: boolean;
}

export const CreateUpdateNotificationTemplateModal: React.FC<Props> = ({
    isActive,
    toggleModal,
    selectedNotificationTemplate,
    isLoading = false,
}) => {
    const [errors, setErrors] = useState<ValidationErrors<CreateOrUpdateNotificationTemplateView>>(
        {}
    );
    const [notificationTemplate, setNotificationTemplate] = useState<
        Partial<CreateOrUpdateNotificationTemplateView>
    >({});

    const ProfessionalFieldOptions = Object.fromEntries(
        Object.entries(ProfessionalFieldFilterFields)
            .filter(([, { value }]) => value !== ProfessionalField.None)
            .map(([, { value, label }]) => [label, label])
    ) as Record<string, string>;

    const createOrUpdateNotificationTemplate = useCreateOrUpdateNotificationTemplate();

    useEffect(() => {
        if (selectedNotificationTemplate) setNotificationTemplate(selectedNotificationTemplate);
        else setNotificationTemplate({});
    }, [selectedNotificationTemplate]);

    const validation = () => {
        const errors = validate(notificationTemplate, ERROR_MESSAGES);
        if (notificationTemplate.name && notificationTemplate.name.length > 150)
            errors.subject = "Naziv predloška ne smije sadržavati više od 150 znakova";
        if (notificationTemplate.subject && notificationTemplate.subject.length > 150)
            errors.subject = "Naslov poruke ne smije sadržavati više od 150 znakova";

        setErrors(errors);

        if (errors)
            document
                .getElementById(FIELD_ID[Object.keys(errors)[0] as keyof typeof FIELD_ID] ?? "")
                ?.scrollIntoView({ behavior: "smooth" });

        return errors;
    };

    const onSubmit = () => {
        if (Object.keys(validation()).length !== 0) return;

        createOrUpdateNotificationTemplate
            .mutateAsync(notificationTemplate as CreateOrUpdateNotificationTemplateView)
            .then(_ => onClose());
    };

    const onClose = () => {
        setErrors({});
        setNotificationTemplate({});
        toggleModal();
    };

    return (
        <Modal
            maintainWidth={40}
            isActive={isActive}
            onClose={onClose}
            modalHeader={
                <ModalHeader
                    onClose={onClose}
                    title={selectedNotificationTemplate ? "Uredi predložak" : "Novi predložak"}
                    isLoading={false}
                />
            }
            modalFooter={
                <ModalFooter>
                    <div className="f-r even g-24 f-a-x-r">
                        <Button
                            type="button"
                            size="small"
                            variant="primary"
                            width="standard"
                            onClick={onSubmit}
                        >
                            SPREMI
                        </Button>
                    </div>
                </ModalFooter>
            }
        >
            {isLoading ? (
                <Loading />
            ) : (
                <div className="communication-template-modal f-c g-24">
                    <div>
                        <Field
                            id={FIELD_ID.name}
                            name="NAZIV PREDLOŠKA"
                            required
                            horizontal
                            errorMessage={errors.name}
                        >
                            <Input
                                value={notificationTemplate.name ?? ""}
                                isValid={!errors.name}
                                onChange={e =>
                                    onChange(
                                        { name: e.target.value },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                id="name"
                                name="name"
                                size="medium"
                                type="text"
                                placeholder="Kreni tipkati..."
                            />
                        </Field>

                        <hr className="separator mt-12 mb-12" />

                        <Field
                            id={FIELD_ID.subject}
                            name="NASLOV PORUKE"
                            required
                            horizontal
                            errorMessage={errors.subject}
                        >
                            <Input
                                value={notificationTemplate.subject ?? ""}
                                isValid={!errors.subject}
                                onChange={e =>
                                    onChange(
                                        { subject: e.target.value },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                id="subject"
                                name="subject"
                                size="medium"
                                type="text"
                                placeholder="Kreni tipkati..."
                            />
                        </Field>

                        <Field id={FIELD_ID.body} name="PORUKA" required errorMessage={errors.body}>
                            <TextArea
                                value={notificationTemplate?.body}
                                height={200}
                                useVariables
                                name="body"
                                onChange={e =>
                                    onChange({ body: e }, setNotificationTemplate, setErrors)
                                }
                            />
                        </Field>
                    </div>

                    <Field
                        id={FIELD_ID.type}
                        name="NAČIN SLANJA"
                        required
                        errorMessage={errors.type}
                    >
                        <Dropdown
                            value={getDropdownValue(
                                notificationTemplate.type,
                                NotificationTypeTranslation
                            )}
                            onChange={item =>
                                onChange({ type: item?.value }, setNotificationTemplate, setErrors)
                            }
                            options={getDropdownOptions(NotificationTypeTranslation)}
                            placeholder="Odaberite način slanja"
                            maxMenuHeight={200}
                            size="medium"
                        />
                    </Field>

                    <div className="f-c g-12">
                        <Field
                            id={FIELD_ID.severity}
                            name="VRSTA PORUKE"
                            horizontal
                            required
                            errorMessage={errors.severity}
                        >
                            <Dropdown
                                value={getDropdownValue(
                                    notificationTemplate?.severity,
                                    NotificationSeverityTranslation
                                )}
                                onChange={item =>
                                    onChange(
                                        { severity: item?.value },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                options={getDropdownOptions(NotificationSeverityTranslation)}
                                placeholder="Odaberite vrstu"
                                maxMenuHeight={200}
                                size="medium"
                            />
                        </Field>
                        <Field
                            id={FIELD_ID.priority}
                            name="PRIORITET PORUKE"
                            horizontal
                            required
                            errorMessage={errors.priority}
                        >
                            <Dropdown
                                value={getDropdownValue(
                                    notificationTemplate.priority,
                                    NotificationPriorityTranslation
                                )}
                                onChange={item =>
                                    onChange(
                                        { priority: item?.value },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                options={getDropdownOptions(NotificationPriorityTranslation)}
                                placeholder="Odaberite prioritet"
                                maxMenuHeight={200}
                                size="medium"
                            />
                        </Field>
                        <Field
                            id={FIELD_ID.professionalField}
                            name="STRUČNO PODRUČJE"
                            horizontal
                            required
                            errorMessage={errors.professionalField}
                        >
                            <Dropdown
                                placeholder="Odaberite stručno područje"
                                value={getDropdownValue(
                                    notificationTemplate?.professionalField,
                                    ProfessionalFieldOptions
                                )}
                                onChange={item =>
                                    onChange(
                                        { professionalField: item?.label },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                maxMenuHeight={200}
                                size="medium"
                                options={getDropdownOptions(ProfessionalFieldOptions)}
                            />
                        </Field>
                        <Field
                            id={FIELD_ID.sendingEntity}
                            name="POŠILJATELJ"
                            horizontal
                            required
                            errorMessage={errors.sendingEntity}
                        >
                            <Dropdown
                                value={getDropdownValue(
                                    notificationTemplate.sendingEntity,
                                    NotificationSendingEntityTranslation
                                )}
                                onChange={item =>
                                    onChange(
                                        { sendingEntity: item?.value },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                options={getDropdownOptions(NotificationSendingEntityTranslation)}
                                placeholder="Odaberite pošiljatelja"
                                maxMenuHeight={200}
                                size="medium"
                            />
                        </Field>
                        <Field horizontal name="JAVNO">
                            <ToggleSwitch
                                onChange={e =>
                                    onChange(
                                        { isShared: e.target.checked },
                                        setNotificationTemplate,
                                        setErrors
                                    )
                                }
                                checked={Boolean(notificationTemplate.isShared)}
                                htmlFor="isShared"
                            />
                        </Field>
                    </div>
                </div>
            )}
        </Modal>
    );
};
